import type { CardDeckId } from "@bing/shared";
import { CARD_DECKS } from "./card-decks";
import { cardFacesStatus, loadCardFaces, subscribeCardFaces } from "./card-faces";
import { MAX_FACE_ATTEMPTS, faceAttemptSrc, faceRetryDelayMs } from "./deck-face-retry";

/**
 * A PICKED DECK IS DOWNLOADED ONCE, IN THE BACKGROUND, AND DRAWN ONLY WHEN ALL OF IT IS IN
 * (2026-10-20, card-decks — the operator: «it must be fully loaded before they reach a table — never
 * white or missing cards, even on a slow connection», and «load it in the background, cache it so
 * it's downloaded only once»).
 *
 * THE RULE, IN ONE LINE: `Card` draws an image deck only when this module says that deck is READY —
 * all 52 faces fetched AND decoded, the `Image` objects held for the session — and draws
 * «استاندارد» until then. So nothing ever waits on an image deck and nothing is ever blank:
 *   - a table that opens before the pick has arrived deals «استاندارد», and the whole hand switches
 *     at once when the last face is in (one store change → one render; every `<img>` it mounts is
 *     already in the memory cache, which Blink attaches synchronously, so there is no frame between);
 *   - a deck that cannot be fetched at all stays «استاندارد» for good, and says so (the sink below);
 *   - an online table never holds a player at a loading screen while the host's deck downloads and
 *     their turn clock runs — the reason a «wait for the deck» gate was not built.
 * Why not per-card, as before: a hand would be half one deck and half another while it loads, and a
 * face that fails mid-game is the 2026-08-31 «سلطنتی blank cards» report.
 *
 * ⚠️ RETRIES USE A FRESH URL, AS `deck-face-retry.ts` RECORDS: re-assigning a failed `src` issues no
 * request. Attempt 0 is the file itself (the cacheable URL); a later attempt adds `?retry=n`, and the
 * URL that succeeded is the one `Card` must draw (`loadedFaceSrc`) — it is the one in the cache.
 * ⚠️ A FEW AT A TIME: 52 parallel transfers on a throttled link is the concurrency that severs them
 * (`card-faces.ts`, single-flight for the same reason). `CONCURRENCY` faces in flight, never more.
 *
 * `standard` is not an image deck: its faces are the `card-faces.ts` chunk, so its status IS that
 * chunk's, and loading it loads the chunk.
 */

export type DeckStatus = "idle" | "loading" | "ready" | "failed";

/** Faces in flight at once. */
export const CONCURRENCY = 3;
/** One attempt that has not settled in this long is abandoned and retried on a fresh URL. */
export const ATTEMPT_TIMEOUT_MS = 90000;

type LoadImage = (src: string) => Promise<unknown>;

interface DeckState {
  status: DeckStatus;
  /** CardId → the URL that loaded (held: `held` keeps the decoded image alive). */
  loaded: Map<string, string>;
  held: unknown[];
  inFlight: Promise<void> | null;
}

const decks = new Map<CardDeckId, DeckState>();
const listeners = new Set<() => void>();

function stateOf(id: CardDeckId): DeckState {
  let s = decks.get(id);
  if (!s) {
    s = { status: "idle", loaded: new Map(), held: [], inFlight: null };
    decks.set(id, s);
  }
  return s;
}

function notify(): void {
  for (const l of listeners) l();
}

/** Subscribe to every deck's status (and the classic chunk's) — `useSyncExternalStore` contract. */
export function subscribeDecks(listener: () => void): () => void {
  listeners.add(listener);
  const off = subscribeCardFaces(listener);
  return () => {
    listeners.delete(listener);
    off();
  };
}

export function deckStatus(id: CardDeckId): DeckStatus {
  if (CARD_DECKS[id]?.faces == null) return cardFacesStatus();
  return stateOf(id).status;
}

/** May `Card` draw this deck? (`standard`: when its chunk is in.) */
export function deckReady(id: CardDeckId): boolean {
  return deckStatus(id) === "ready";
}

/** The URL that actually loaded for this face (the one in the cache), or null if it has not. */
export function loadedFaceSrc(id: CardDeckId, cardId: string): string | null {
  return decks.get(id)?.loaded.get(cardId) ?? null;
}

const defaultLoadImage: LoadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      img.src = ""; // abandon the transfer
      reject(new Error(`deck face timed out: ${src}`));
    }, ATTEMPT_TIMEOUT_MS);
    img.onload = () => {
      if (settled) return;
      // DECODED, not just fetched: `decode()` resolves when the picture can be painted. A rejection
      // after a good load (Safari has rejected `decode()` for SVG) is not a failed face.
      const done = (): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(img);
      };
      if (typeof img.decode === "function") img.decode().then(done, done);
      else done();
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error(`deck face failed: ${src}`));
    };
    img.src = src;
  });

let loadImage: LoadImage = defaultLoadImage;
let sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Told when a deck spends every attempt on some face — wired to the beacon in `main.tsx`. */
export type DeckLoadFailure = { deck: CardDeckId; url: string; attempts: number };
let onTerminalFailure: (failure: DeckLoadFailure) => void = () => undefined;

/**
 * Injected, not imported — the `card-faces.ts` reasoning: importing `client-report` here would
 * drag the Telegram SDK into a framework-free module every table test instantiates.
 */
export function setDeckFailureSink(sink: (failure: DeckLoadFailure) => void): void {
  onTerminalFailure = sink;
}

/** One face, every attempt; resolves with the URL that loaded, rejects when all are spent. */
async function loadFace(s: DeckState, cardId: string, url: string): Promise<void> {
  let lastSrc = url;
  for (let n = 0; n <= MAX_FACE_ATTEMPTS; n++) {
    const src = faceAttemptSrc(url, n);
    lastSrc = src;
    try {
      const img = await loadImage(src);
      s.held.push(img);
      s.loaded.set(cardId, src);
      return;
    } catch {
      if (n < MAX_FACE_ATTEMPTS) await sleep(faceRetryDelayMs(n));
    }
  }
  throw Object.assign(new Error(`deck face failed: ${lastSrc}`), { src: lastSrc });
}

async function run(id: CardDeckId, s: DeckState): Promise<void> {
  const faces = CARD_DECKS[id].faces ?? {};
  // only what is missing: a round after a failure does not re-fetch what is already held
  const queue = Object.entries(faces).filter(([cardId]) => !s.loaded.has(cardId));
  // the first face that spent every attempt (a holder: workers set it from their closures)
  const failure: { src: string | null } = { src: null };
  const worker = async (): Promise<void> => {
    for (;;) {
      const next = queue.shift();
      if (!next || failure.src !== null) return;
      try {
        await loadFace(s, next[0], next[1]);
      } catch (err) {
        if (failure.src === null) failure.src = (err as { src?: string }).src ?? next[1];
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  if (failure.src !== null || s.loaded.size < Object.keys(faces).length) {
    s.status = "failed";
    notify();
    onTerminalFailure({ deck: id, url: failure.src ?? "", attempts: MAX_FACE_ATTEMPTS + 1 });
    throw new Error(`deck ${id}: load failed`);
  }
  s.status = "ready";
  notify();
}

/**
 * Load a deck — at most one round at a time per deck; resolves when it is READY. A round that fails
 * clears itself so the next call starts a fresh one (the `card-faces.ts` contract).
 */
export function loadDeck(id: CardDeckId): Promise<void> {
  if (CARD_DECKS[id]?.faces == null) return loadCardFaces().then(() => undefined);
  const s = stateOf(id);
  if (s.status === "ready") return Promise.resolve();
  if (s.inFlight) return s.inFlight;
  s.status = "loading";
  notify();
  s.inFlight = run(id, s).finally(() => {
    s.inFlight = null;
  });
  return s.inFlight;
}

/** Warm a deck in the background (never throws). */
export function prefetchDeck(id: CardDeckId): void {
  void loadDeck(id).catch(() => undefined);
}

/**
 * One face only — the deck picker's sample (`KD`), so a picker can show a deck's look without
 * downloading all of it. Resolves true when that face is held.
 */
export async function loadDeckSample(id: CardDeckId, cardId: string): Promise<boolean> {
  const faces = CARD_DECKS[id]?.faces;
  if (!faces)
    return loadCardFaces().then(
      () => true,
      () => false,
    );
  const s = stateOf(id);
  if (s.loaded.has(cardId)) return true;
  const url = faces[cardId];
  if (!url) return false;
  try {
    await loadFace(s, cardId, url);
    notify();
    return true;
  } catch {
    return false;
  }
}

/**
 * A face `Card` drew could not be painted after all (an `<img>` error on a URL this module had
 * loaded — not expected, since it is held, but not trusted either): the deck stops being ready, so
 * every card goes back to «استاندارد» at once, and a fresh round re-fetches what is missing.
 */
export function markDeckFaceBroken(id: CardDeckId, cardId: string): void {
  const s = decks.get(id);
  if (!s || CARD_DECKS[id]?.faces == null) return;
  s.loaded.delete(cardId);
  if (s.status === "ready") s.status = "idle";
  notify();
  prefetchDeck(id);
}

// ------------------------------------------------------------------------------------ test seams

export function __setDeckLoaderForTests(
  next: LoadImage,
  fakeSleep?: (ms: number) => Promise<void>,
): void {
  loadImage = next;
  if (fakeSleep) sleep = fakeSleep;
}

/** Mark a deck fully loaded (every face at its own URL) — table tests render it synchronously. */
export function __setDeckReadyForTests(id: CardDeckId): void {
  const s = stateOf(id);
  for (const [cardId, url] of Object.entries(CARD_DECKS[id].faces ?? {})) s.loaded.set(cardId, url);
  s.status = "ready";
  notify();
}

export function __resetDecksForTests(): void {
  decks.clear();
  loadImage = defaultLoadImage;
  sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  onTerminalFailure = () => undefined;
  notify();
}
